import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ArrowLeft, Search, Layers, Loader2, Tag, Calendar, Sparkles, Eye } from 'lucide-react';
import { getPublicCollectionsByPhone } from '../api/fabricCollection.api';
import FabricCatalogueView from './FabricCatalogueView';

const SubscribedUserCollectionsView = ({ 
  phoneNumber, 
  userName, 
  onBack,
  showNotification
}) => {
  const [collections, setCollections] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [userInfo, setUserInfo] = useState(null);
  const [error, setError] = useState(null);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const observerTarget = useRef(null);
  const searchTimeoutRef = useRef(null);
  const isHandlingPopState = useRef(false);

  // Normalize phone number (remove non-digits)
  const normalizedPhone = phoneNumber ? phoneNumber.replace(/\D/g, '') : '';

  // Fetch fabric collections with pagination
  const fetchCollections = useCallback(async (page = 1, append = false) => {
    if (!normalizedPhone) return;
    
    if (append) {
      setIsLoadingMore(true);
    } else {
      setIsLoading(true);
      setError(null);
    }
    
    try {
      const response = await getPublicCollectionsByPhone(normalizedPhone, page, 20, searchQuery);
      const { collections: fetchedCollections, pagination, user } = response;
      
      if (append) {
        setCollections(prev => [...prev, ...fetchedCollections]);
      } else {
        setCollections(fetchedCollections);
      }
      
      setUserInfo(user);
      setCurrentPage(pagination.currentPage);
      setHasNextPage(pagination.hasNextPage);
    } catch (error) {
      console.error('Error fetching collections:', error);
      setError(error.response?.data?.message || 'Failed to load collections');
      if (!append) {
        setCollections([]);
      }
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  }, [normalizedPhone, searchQuery]);

  // Initial load
  useEffect(() => {
    if (normalizedPhone) {
      fetchCollections(1, false);
    }
  }, [normalizedPhone, fetchCollections]);

  // Debounce search input
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setSearchQuery(searchInput);
      setCurrentPage(1);
    }, 500);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchInput]);

  // Fetch collections when search query changes
  useEffect(() => {
    if (normalizedPhone) {
      fetchCollections(1, false);
    }
  }, [searchQuery, normalizedPhone, fetchCollections]);

  // Infinite scroll
  useEffect(() => {
    if (!hasNextPage || isLoadingMore || isLoading) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting && hasNextPage && !isLoadingMore && !isLoading) {
          fetchCollections(currentPage + 1, true);
        }
      },
      { 
        threshold: 0.1,
        rootMargin: '100px'
      }
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
      observer.disconnect();
    };
  }, [hasNextPage, isLoadingMore, isLoading, currentPage, fetchCollections]);

  // Lazy image component
  const LazyImage = ({ src, alt, className }) => {
    const [imageSrc, setImageSrc] = useState(null);
    const [isLoaded, setIsLoaded] = useState(false);

    useEffect(() => {
      const img = new Image();
      img.src = src;
      img.onload = () => {
        setImageSrc(src);
        setIsLoaded(true);
      };
      img.onerror = () => {
        setImageSrc(null);
        setIsLoaded(false);
      };
    }, [src]);

    if (!isLoaded || !imageSrc) {
      return (
        <div className={`${className} bg-gradient-to-br from-purple-50 via-pink-50 to-indigo-50 flex items-center justify-center`}>
          <Layers size={24} className="text-purple-300" />
        </div>
      );
    }

    return <img src={imageSrc} alt={alt} className={className} />;
  };

  const handleOpenCollection = (collection) => {
    setSelectedCollection(collection);
    // Push state to browser history
    window.history.pushState(
      { view: 'subscribed-collection-detail', collectionId: collection._id },
      '',
      window.location.pathname
    );
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBackToGrid = () => {
    if (isHandlingPopState.current) {
      isHandlingPopState.current = false;
      return;
    }
    setSelectedCollection(null);
    // Only go back if there's history
    if (window.history.state?.view === 'subscribed-collection-detail') {
      window.history.back();
    }
  };

  // Listen for browser back button
  useEffect(() => {
    const handlePopState = (event) => {
      if (selectedCollection) {
        isHandlingPopState.current = true;
        setSelectedCollection(null);
      }
    };

    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [selectedCollection]);

  // Collection Card Component
  const CollectionCard = React.memo(({ collection, onOpen }) => {
    const collectionImage = collection.image?.url || null;
    const fabricCount = collection.fabrics?.length || 0;
    const tags = collection.tags || [];
    const propertyEntries = (collection.properties || []).map((prop) => {
      if (prop.key && prop.value) return `${prop.key}: ${prop.value}`;
      return prop.key || prop.value || '';
    }).filter(Boolean);
    const highlightProps = propertyEntries.slice(0, 2);

    return (
      <div
        className="group bg-white/90 backdrop-blur border border-white/80 rounded-2xl shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 overflow-hidden cursor-pointer"
        onClick={() => onOpen?.(collection)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onOpen?.(collection);
          }
        }}
      >
        <div className="relative aspect-[4/5] bg-gradient-to-br from-purple-50 via-pink-50 to-indigo-50 overflow-hidden">
          {collectionImage ? (
            <LazyImage
              src={collectionImage}
              alt={collection.name}
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
            />
          ) : (
            <div className="flex flex-col items-center justify-center w-full h-full px-4">
              <span className="text-base sm:text-lg font-semibold text-purple-600 text-center break-words">
                {collection.name}
              </span>
            </div>
          )}
          <div className="absolute top-2 left-2 text-[10px] sm:text-xs font-semibold px-2.5 py-0.5 rounded-full bg-purple-600 text-white shadow">
            Collection
          </div>
          <div className="absolute top-2 right-2 text-[10px] sm:text-xs font-semibold px-2.5 py-0.5 rounded-full bg-blue-500 text-white shadow">
            {fabricCount} {fabricCount === 1 ? 'fabric' : 'fabrics'}
          </div>
        </div>

        <div className="p-3 sm:p-4 space-y-2">
          <div>
            <h3 className="text-sm font-semibold text-gray-900 line-clamp-1">{collection.name}</h3>
            {highlightProps.length ? (
              <div className="mt-1 flex flex-wrap gap-1">
                {highlightProps.map((label, index) => (
                  <span
                    key={`${label}-${index}`}
                    className="px-2 py-0.5 text-[10px] font-medium rounded-full bg-gray-100 text-gray-600"
                  >
                    {label}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-xs text-gray-500 mt-1">No custom properties yet</p>
            )}
          </div>
          {tags.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {tags.slice(0, 3).map(tag => (
                <span
                  key={tag}
                  className="text-[10px] px-2 py-0.5 bg-purple-50 text-purple-600 rounded-full font-medium"
                >
                  #{tag}
                </span>
              ))}
              {tags.length > 3 && (
                <span className="text-[10px] text-purple-500 font-semibold">
                  +{tags.length - 3}
                </span>
              )}
            </div>
          )}
          <div className="flex items-center justify-between text-[11px] text-gray-400">
            <span className="text-purple-500 font-medium">{fabricCount} fabrics</span>
          </div>
        </div>
      </div>
    );
  });

  if (selectedCollection) {
    return (
      <SubscribedCollectionDetail
        collection={selectedCollection}
        onBack={handleBackToGrid}
        LazyImageComponent={LazyImage}
        showNotification={showNotification}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-white/80 backdrop-blur border-b border-white/60 shadow-sm">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 py-4 flex items-center gap-3 sm:gap-4">
          <button
            onClick={onBack}
            className="p-2 rounded-full bg-white/60 hover:bg-white transition-colors shadow-sm"
            aria-label="Back to subscriptions"
          >
            <ArrowLeft size={18} className="text-gray-700" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg sm:text-2xl font-semibold text-gray-900 truncate">
              {userName || userInfo?.name || 'Collections'}
            </h1>
            <p className="text-xs sm:text-sm text-gray-500">
              {collections.length} {collections.length === 1 ? 'collection' : 'collections'}
            </p>
          </div>
        </div>
      </div>

      {/* Search Bar */}
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 py-4">
        <div className="relative">
          <Search size={18} className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search collections..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="w-full pl-11 pr-4 py-3 bg-white/90 border border-white rounded-2xl shadow-inner text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 pb-6">
        {isLoading && collections.length === 0 ? (
          <div className="flex items-center justify-center py-12 sm:py-16">
            <div className="text-center">
              <Loader2 size={48} className="text-blue-500 animate-spin mx-auto mb-4" />
              <p className="text-gray-600 text-sm sm:text-base">Loading collections...</p>
            </div>
          </div>
        ) : error ? (
          <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-8 sm:p-12 text-center">
            <p className="text-red-600 text-sm sm:text-base">{error}</p>
            <button
              onClick={() => fetchCollections(1, false)}
              className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm sm:text-base"
            >
              Try Again
            </button>
          </div>
        ) : collections.length === 0 ? (
          <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-8 sm:p-12 text-center">
            <Layers size={48} className="text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg sm:text-xl font-semibold text-gray-700 mb-2">No Collections Found</h3>
            <p className="text-sm sm:text-base text-gray-500">
              {searchQuery ? 'Try a different search term.' : 'This user has no public collections yet.'}
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4">
              {collections.map((collection) => (
                <CollectionCard key={collection._id} collection={collection} onOpen={handleOpenCollection} />
              ))}
            </div>
            
            {/* Infinite scroll trigger */}
            {hasNextPage && (
              <div ref={observerTarget} className="py-6 flex justify-center">
                {isLoadingMore && (
                  <Loader2 size={32} className="text-blue-500 animate-spin" />
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const SubscribedCollectionDetail = ({ collection, onBack, LazyImageComponent, showNotification }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFabric, setSelectedFabric] = useState(null);
  const isHandlingPopState = useRef(false);

  const fabricCount = collection?.fabrics?.length || 0;
  const tags = collection?.tags || [];
  const properties = collection?.properties || [];
  const createdDate = collection?.createdAt
    ? new Date(collection.createdAt).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      })
    : null;

  const propertyChips = properties
    .map((prop) => {
      if (prop.key && prop.value) return `${prop.key}: ${prop.value}`;
      return prop.key || prop.value || '';
    })
    .filter(Boolean);

  const filteredFabrics = (collection?.fabrics || []).filter((fabric = {}) => {
    if (!searchTerm.trim()) return true;
    const normalized = searchTerm.toLowerCase();
    return (
      fabric.name?.toLowerCase().includes(normalized) ||
      fabric.description?.toLowerCase().includes(normalized) ||
      fabric.category?.toLowerCase().includes(normalized) ||
      fabric.productType?.toLowerCase().includes(normalized)
    );
  });

  const getFabricImage = (fabric) => {
    if (!fabric) return null;
    return (
      fabric.image ||
      fabric.images?.original?.s3Url ||
      fabric.images?.original?.imgbbUrl ||
      fabric.images?.original?.url ||
      fabric.images?.preview?.s3Url ||
      fabric.images?.preview?.url ||
      null
    );
  };

  const handleGenerateClick = (fabric) => {
    if (!fabric) return;

    const token = localStorage.getItem('token');
    const variants = [];

    const primaryImage =
      fabric.images?.original?.s3Url ||
      fabric.images?.original?.imgbbUrl ||
      fabric.images?.original?.url ||
      fabric.image;

    if (primaryImage) {
      variants.push({
        image: { s3Url: primaryImage },
        colorName: 'Primary',
        isPrimary: true
      });
    }

    if (fabric.colorVariants && fabric.colorVariants.length > 0) {
      fabric.colorVariants.forEach((variant, index) => {
        variants.push({
          ...variant,
          isPrimary: false,
          originalIndex: index
        });
      });
    }

    const productDetails = {
      name: fabric.name || '',
      description: fabric.description || '',
      image: primaryImage || '',
      category: fabric.category || '',
      type: fabric.type || '',
      targetAudience: fabric.targetAudience || 'General Audience',
      fabricAnalysis: fabric.fabricAnalysis || null,
      selectedProduct: fabric,
      colorVariants: fabric.colorVariants || [],
      selectedVariants: variants,
      selectedVariantIndex: 0
    };

    localStorage.setItem('selectedFabricForGeneration', JSON.stringify(productDetails));

    if (!token) {
      showNotification?.('Please login to generate kits with this fabric', 'info');
    }

    window.location.href = '/';
  };

  // Handle opening fabric detail and push history state
  const handleOpenFabric = useCallback((fabric) => {
    setSelectedFabric(fabric);
    // Push state to browser history
    window.history.pushState(
      { view: 'subscribed-fabric-detail', fabricId: fabric._id },
      '',
      window.location.pathname
    );
  }, []);

  // Handle closing fabric detail
  const handleCloseFabric = useCallback(() => {
    if (isHandlingPopState.current) {
      isHandlingPopState.current = false;
      return;
    }
    setSelectedFabric(null);
    // Only go back if there's history
    if (window.history.state?.view === 'subscribed-fabric-detail') {
      window.history.back();
    }
  }, []);

  // Listen for browser back button
  useEffect(() => {
    const handlePopState = (event) => {
      if (selectedFabric) {
        isHandlingPopState.current = true;
        setSelectedFabric(null);
      }
    };

    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [selectedFabric]);

  if (selectedFabric) {
    return (
      <FabricCatalogueView
        fabric={selectedFabric}
        onClose={handleCloseFabric}
        showNotification={showNotification}
        allowShadeCardUpload={false}
        onGenerateFabric={handleGenerateClick}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      <div className="bg-gradient-to-r from-purple-600 to-indigo-500 text-white shadow-lg">
        <div className="max-w-6xl mx-auto px-3 sm:px-4 lg:px-6 py-5 space-y-4">
          <button
            onClick={onBack}
            className="p-2 rounded-full bg-white/10 hover:bg-white/25 transition-colors"
            aria-label="Back to collections"
          >
            <ArrowLeft size={18} />
          </button>
          <div className="bg-white rounded-3xl shadow-2xl p-5 sm:p-6 lg:p-7 flex flex-col sm:flex-row gap-6 items-center">
            <div className="w-36 sm:w-44 lg:w-52 h-48 sm:h-56 lg:h-64 rounded-2xl overflow-hidden shadow-lg flex-shrink-0">
              {collection?.image?.url ? (
                <img
                  src={collection.image.url}
                  alt={collection.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-purple-50 via-pink-50 to-indigo-50 flex items-center justify-center px-4">
                  <span className="text-base sm:text-lg font-semibold text-purple-600 text-center break-words">
                    {collection?.name}
                  </span>
                </div>
              )}
            </div>

            <div className="flex-1 space-y-4 text-gray-800 w-full">
              <div>
                <p className="text-xs uppercase tracking-wide text-purple-400 font-semibold">
                  {fabricCount} fabric{fabricCount === 1 ? '' : 's'} in this collection
                </p>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mt-1">
                  {collection?.name || 'Collection'}
                </h1>
                {createdDate && (
                  <p className="text-sm text-gray-500 flex items-center gap-1.5 mt-1">
                    <Calendar size={14} className="text-purple-500" />
                    Created on {createdDate}
                  </p>
                )}
              </div>

              {propertyChips.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Properties</h3>
                  <div className="flex flex-wrap gap-2">
                    {propertyChips.map((label, index) => (
                      <span
                        key={`${label}-${index}`}
                        className="px-3 py-1 text-xs font-medium bg-purple-50 text-purple-600 rounded-full"
                      >
                        {label}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {tags.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                    <Tag size={16} className="text-purple-500" />
                    Tags
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {tags.map((tag) => (
                      <span
                        key={tag}
                        className="text-xs px-3 py-1 bg-purple-100 text-purple-700 rounded-full font-medium"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-3 sm:px-4 lg:px-6 py-6 space-y-6">
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-4 sm:p-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Fabrics in this collection ({filteredFabrics.length})
              </h2>
              <p className="text-sm text-gray-500">Browse materials uploaded by the creator</p>
            </div>
            <div className="relative w-full sm:w-80">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search fabrics by name, description..."
                className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {fabricCount === 0 ? (
            <div className="text-center py-10 text-gray-500">
              <Layers size={40} className="mx-auto mb-3 text-gray-300" />
              <p>No public fabrics are assigned to this collection yet.</p>
            </div>
          ) : filteredFabrics.length === 0 ? (
            <div className="text-center py-10 text-gray-500">
              <Layers size={40} className="mx-auto mb-3 text-gray-300" />
              <p>No fabrics match your search.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4">
              {filteredFabrics.map((fabric) => {
                const fabricImage = getFabricImage(fabric);
                return (
                  <div
                    key={fabric?._id || fabric?.name}
                    className="group bg-white rounded-lg sm:rounded-xl border border-gray-200 hover:border-blue-300 hover:shadow-lg transition-all duration-300 overflow-hidden flex flex-col"
                  >
                    <div
                      className="relative aspect-square bg-gray-100 cursor-pointer overflow-hidden"
                      onClick={() => handleOpenFabric(fabric)}
                      title="View fabric details"
                    >
                      {fabricImage ? (
                        <LazyImageComponent
                          src={fabricImage}
                          alt={fabric?.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      ) : (
                        <div className="h-full flex items-center justify-center text-purple-400 font-semibold px-4 text-center">
                          {fabric?.name || 'Untitled'}
                        </div>
                      )}
                    </div>

                    <div className="p-3 sm:p-4 flex-1 flex flex-col gap-2">
                      <div>
                        <h3 className="font-semibold text-gray-800 mb-1 line-clamp-1 text-sm sm:text-base">
                          {fabric?.name || 'Untitled Fabric'}
                        </h3>
                        <p className="text-xs sm:text-sm text-gray-500 line-clamp-2">
                          {fabric?.description || 'Fabric material uploaded from collection'}
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-1 text-[11px] text-gray-500">
                        {fabric?.category && (
                          <span className="px-2 py-0.5 bg-gray-100 rounded-full">
                            {fabric.category}
                          </span>
                        )}
                        {fabric?.productType && (
                          <span className="px-2 py-0.5 bg-gray-100 rounded-full">
                            {fabric.productType}
                          </span>
                        )}
                      </div>
                      <div className="mt-auto flex gap-2">
                        <button
                          onClick={() => handleGenerateClick(fabric)}
                          className="flex-1 bg-blue-600 text-white px-3 py-2 rounded-lg text-xs sm:text-sm font-medium flex items-center justify-center gap-1.5 hover:bg-blue-700 transition-colors"
                        >
                          <Sparkles size={14} />
                          Generate
                        </button>
                        <button
                          onClick={() => handleOpenFabric(fabric)}
                          className="px-3 py-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors"
                        >
                          <Eye size={14} />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubscribedUserCollectionsView;

